import { useCallback } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

import { prepareOptionSiteValues } from '@utils/kpi-utils/common';
import { changeOptionsByVendor } from '@components/Kpi/Parameters/utils';

import { IKPISite, TResetSitesAndVendorArgs } from '@interfaces/kpi/kpiCommon';
import { IGroupSelectOptions, TOnChangeSites } from '@components/common/GroupSelect/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { ETechnologiesValues, TVendorsValues } from '@enums/common';
import { EFormFields } from '@enums/kpi-api';

interface UseSitesAndVendorLogicProps {
	setValue: UseFormSetValue<FieldValues>;
	optionsVendorsKpi: Array<TSelectOptionType & { isDefault: boolean }>;
	onChangeSitesT_5G: (...params: TOnChangeSites) => void;
	onChangeSitesT_4G_FDD: (...params: TOnChangeSites) => void;
	onChangeSitesT_4G_TDD: (...params: TOnChangeSites) => void;
	resetT_5G: () => void;
	resetT_4G_FDD: () => void;
	resetT_4G_TDD: () => void;
	options4GFddLimenet: Array<IGroupSelectOptions>;
	options4GFddNokia: Array<IGroupSelectOptions>;
	options4GTddLimenet: Array<IGroupSelectOptions>;
	options4GTddNokia: Array<IGroupSelectOptions>;
	options5GLimenet: Array<IGroupSelectOptions>;
	options5GNokia: Array<IGroupSelectOptions>;
	options5GOran: Array<IGroupSelectOptions>;
	vendor: { name?: TVendorsValues; value: TVendorsValues };
}

export const useSitesAndVendorLogic = ({
	setValue,
	optionsVendorsKpi,
	onChangeSitesT_5G,
	onChangeSitesT_4G_FDD,
	onChangeSitesT_4G_TDD,
	resetT_5G,
	resetT_4G_FDD,
	resetT_4G_TDD,
	options4GFddLimenet,
	options4GFddNokia,
	options4GTddLimenet,
	options4GTddNokia,
	options5GLimenet,
	options5GNokia,
	options5GOran,
	vendor,
}: UseSitesAndVendorLogicProps) => {
	const resetSitesAndVendor = useCallback((...params: TResetSitesAndVendorArgs) => {
		const [selectedValues, isUpdateDirty = true] = params;

		const options5G = changeOptionsByVendor({
			vendor: { value: selectedValues.vendor },
			optionsLimenet: options5GLimenet,
			options5GOran,
			optionsNokia: options5GNokia,
		});

		const options4GFdd = changeOptionsByVendor({
			vendor: { value: selectedValues.vendor },
			optionsLimenet: options4GFddLimenet,
			optionsNokia: options4GFddNokia,
		});

		const options4GTdd = changeOptionsByVendor({
			vendor: { value: selectedValues.vendor },
			optionsLimenet: options4GTddLimenet,
			optionsNokia: options4GTddNokia,
		});

		if (selectedValues.vendor !== vendor?.value) {
			setValue(EFormFields.vendor, optionsVendorsKpi.find(vendor => vendor.value === selectedValues.vendor));
		}
		if (selectedValues.sites) {
			const T_5G_Sites: Array<IKPISite>
				= selectedValues.sites.filter((site: IKPISite) => site.technology === ETechnologiesValues.T_5G);
			const T_4G_FDD_Sites: Array<IKPISite>
				= selectedValues.sites.filter((site: IKPISite) => site.technology === ETechnologiesValues.T_4G_FDD);
			const T_4G_TDD_Sites: Array<IKPISite>
				= selectedValues.sites.filter((site: IKPISite) => site.technology === ETechnologiesValues.T_4G_TDD);

			if (T_5G_Sites.length) {
				const checkedValuesT_5G = prepareOptionSiteValues({
					options: options5G,
					sitesByTechnology: T_5G_Sites,
				});

				onChangeSitesT_5G(checkedValuesT_5G, isUpdateDirty);
			} else {
				resetT_5G();
			}

			if (T_4G_FDD_Sites.length) {
				const checkedValuesT_4G_FDD = prepareOptionSiteValues({
					options: options4GFdd,
					sitesByTechnology: T_4G_FDD_Sites,
				});

				onChangeSitesT_4G_FDD(checkedValuesT_4G_FDD, isUpdateDirty);
			} else {
				resetT_4G_FDD();
			}

			if (T_4G_TDD_Sites.length) {
				const checkedValuesT_4G_TDD = prepareOptionSiteValues({
					options: options4GTdd,
					sitesByTechnology: T_4G_TDD_Sites,
				});

				onChangeSitesT_4G_TDD(checkedValuesT_4G_TDD, isUpdateDirty);
			} else {
				resetT_4G_TDD();
			}
		}
	}, [
		onChangeSitesT_4G_FDD,
		onChangeSitesT_4G_TDD,
		onChangeSitesT_5G,
		options4GFddLimenet,
		options4GFddNokia,
		options4GTddLimenet,
		options4GTddNokia,
		options5GLimenet,
		options5GNokia,
		options5GOran,
		optionsVendorsKpi,
		resetT_4G_FDD,
		resetT_4G_TDD,
		resetT_5G,
		setValue,
		vendor?.value,
	]);

	return { resetSitesAndVendor };
};
